import { ExcelButton } from "@/controls/buttons/ExcelButton";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { ATHENS_GRAY } from "@/shared/colors";
import { ErrorModal, HelmetDexteel, getMomentTz } from "@dexteel/mesf-core";
import { Grid2, Paper, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { GridApi } from "ag-grid-community";
import { Moment } from "moment-timezone";
import { useState } from "react";
import { DelaysReportTable } from "./components/DelaysReportTable";
import { Filters } from "./components/filters/Filters";
import { DelayByShift } from "./models/DelayByShift";
import { getDelays } from "./repositories/DelaysReportRepository";

const moment = getMomentTz();

type useDelaysProps = {
	startDate: Date;
	endDate: Date;
	delayAreaAssetId?: number | null;
	onError: (error: Error) => void;
};

export const useDelays = ({
	startDate,
	endDate,
	delayAreaAssetId,
	onError,
}: useDelaysProps) => {
	return useQuery<DelayByShift[], Error>({
		queryKey: ["delays", startDate, endDate, delayAreaAssetId],
		queryFn: () =>
			getDelays({
				startDate,
				endDate,
				delayAreaAssetId,
			}),
		onError: onError,
		enabled: true,
	});
};

export const DelaysReportPage = () => {
	const [error, setError] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");

	const [filterStartDate, setFilterStartDate] = useState<Moment>(
		moment().subtract(7, "days").startOf("day"),
	);
	const [filterEndDate, setFilterEndDate] = useState<Moment>(
		moment().endOf("day"),
	);
	const [filterLine, setFilterLine] = useState<number | null>(null);

	const [gridApi, setGridApi] = useState<GridApi | null>(null);

	// filterLine === null → "All" → SP receives null and skips area filter
	const { data: delays, isLoading } = useDelays({
		startDate: filterStartDate.toDate(),
		endDate: filterEndDate.toDate(),
		delayAreaAssetId: filterLine,
		onError: (e) => setError(e.message),
	});

	const handleFilterChange = (startDate: Moment, endDate: Moment) => {
		setFilterStartDate(startDate);
		setFilterEndDate(endDate);
	};

	const handleExportToExcel = () => {
		if (gridApi) {
			const params = {
				skipHeader: false,
				skipFooters: true,
				skipGroups: true,
				fileName: "delays_report.xlsx",
			};
			gridApi.exportDataAsExcel(params);
		} else {
			setError("Unable to export data. Please try again.");
		}
	};

	return (
		<>
			<HelmetDexteel title="Delays Report" />
			<Grid2
				container
				size={{ md: 12, xs: 12 }}
				spacing={1}
				px={2}
				pt={2}
				style={{ backgroundColor: ATHENS_GRAY }}
			>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" style={{ fontWeight: 600 }}>
						Delays Report
					</Typography>
				</Grid2>

				<Grid2 size={{ xs: "auto" }}>
					<Paper
						elevation={1}
						sx={{
							padding: 2,
							height: "100%",
							display: "flex",
							alignItems: "center",
						}}
					>
						<Filters
							onFilterChange={handleFilterChange}
							initialStartDate={filterStartDate}
							initialEndDate={filterEndDate}
							filterLine={filterLine}
							onFilterLineChange={setFilterLine}
						/>
					</Paper>
				</Grid2>
				<Grid2 size={{ xs: "grow" }} />
				<Grid2
					size={{ xs: "auto" }}
					sx={{
						display: "flex",
						alignItems: "center",
					}}
				>
					<ExcelButton
						handleExportToExcel={handleExportToExcel}
						disabled={!delays || delays.length === 0}
					/>
				</Grid2>

				<Grid2 size={{ md: 12, xs: 12 }}>
					<DelaysReportTable
						isLoading={isLoading}
						rows={delays || []}
						setGridApi={setGridApi}
					/>
				</Grid2>
			</Grid2>
			<ErrorModal
				error={error}
				onHide={() => {
					setError("");
				}}
			/>
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
		</>
	);
};
